import { useEffect, useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import type { HotelSearchData, HotelSearchProgress, SearchStatus } from '@/types/hotel-search';

interface UseHotelSearchProgressOptions {
  enabled: boolean;
  onCompleted?: () => void;
  onRawDataUpdate?: (data: HotelSearchData) => void;
}

const EMPTY_PROGRESS: HotelSearchProgress = {
  status: 'processing',
  progress_total: 0,
  progress_done: 0,
  progress_log: [],
};

export function useHotelSearchProgress(
  searchId: string,
  { enabled, onCompleted, onRawDataUpdate }: UseHotelSearchProgressOptions,
) {
  const [progress, setProgress] = useState<HotelSearchProgress>(EMPTY_PROGRESS);

  const applyRow = useCallback((row: {
    status?: string;
    progress_total?: number | null;
    progress_done?: number | null;
    progress_log?: unknown;
    raw_data?: unknown;
  }) => {
    const next: HotelSearchProgress = {
      status: (row.status ?? 'processing') as SearchStatus,
      progress_total: row.progress_total ?? 0,
      progress_done: row.progress_done ?? 0,
      progress_log: (row.progress_log ?? []) as HotelSearchProgress['progress_log'],
    };
    setProgress(next);

    if (row.raw_data) {
      onRawDataUpdate?.(row.raw_data as HotelSearchData);
    }

    if (next.status === 'completed' || next.status === 'failed') {
      onCompleted?.();
    }

    return next;
  }, [onCompleted, onRawDataUpdate]);

  useEffect(() => {
    if (!enabled || !searchId) return;

    let channel: ReturnType<typeof supabase.channel> | null = null;

    async function loadInitial() {
      const { data } = await supabase
        .from('hotel_searches')
        .select('status, progress_total, progress_done, progress_log, raw_data')
        .eq('search_id', searchId)
        .maybeSingle();

      if (data) {
        applyRow(data);
      }

      channel = supabase
        .channel(`hotel-search-progress-${searchId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'hotel_searches',
            filter: `search_id=eq.${searchId}`,
          },
          payload => {
            applyRow(payload.new as Parameters<typeof applyRow>[0]);
          },
        )
        .subscribe();
    }

    loadInitial();
    return () => { channel?.unsubscribe(); };
  }, [searchId, enabled, applyRow]);

  return progress;
}
